"use client"

import { motion, AnimatePresence } from "framer-motion"
import { X, Pickaxe, Mountain, Gem, Calendar, FileText, Users } from "lucide-react"
import { useState, useEffect } from "react"

export function ReportDetailsModal({ report, formatDate, onClose }) {
  const [imageUrls, setImageUrls] = useState([])
  const [loadingImages, setLoadingImages] = useState(false)
  const [imageError, setImageError] = useState(null)
  const [activeImageIndex, setActiveImageIndex] = useState(null)

  useEffect(() => {
    if (!report) return

    const BASE_URL = "https://backend.emerald.azadnoori.com"

    const fetchImages = async () => {
      if (!report?.images || !Array.isArray(report.images) || report.images.length === 0) {
        setImageUrls([])
        setImageError("No images associated with this report.")
        return
      }

      setLoadingImages(true)
      setImageError(null)

      const urls = await Promise.all(
        report.images.map(async (photo, index) => {
          try {
            const normalizedPhoto = photo.replace("/Uploads", "/uploads") // Normalize case
            const url = normalizedPhoto.startsWith("http")
              ? normalizedPhoto
              : `${BASE_URL}${normalizedPhoto}?t=${Date.now()}`
            const response = await fetch(url, {
              credentials: "include",
            })
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }
            const blob = await response.blob()
            const blobUrl = URL.createObjectURL(blob)
            return blobUrl
          } catch (error) {
            return "/placeholder.svg?height=192&width=192&text=Image+Failed"
          }
        }),
      )
      setImageUrls(urls)
      setLoadingImages(false)
      return () => urls.forEach(URL.revokeObjectURL)
    }
    fetchImages()
  }, [report])

  const openLightbox = (index) => {
    setActiveImageIndex(index)
  }

  const closeLightbox = () => {
    setActiveImageIndex(null)
  }

  const nextImage = (e) => {
    e.stopPropagation()
    setActiveImageIndex((prev) => (prev + 1) % imageUrls.length)
  }

  const prevImage = (e) => {
    e.stopPropagation()
    setActiveImageIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length)
  }

  if (!report) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            {/* Header with gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-600/90 to-emerald-700/90 h-24 md:h-32 z-0" />

            <div className="relative z-10 flex justify-between items-center p-4 md:p-6">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="bg-white p-1.5 md:p-2 rounded-full shadow-md">
                  <Calendar className="h-5 w-5 md:h-6 md:w-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-white">Mining Report</h2>
                  <p className="text-sm md:text-base text-emerald-100">{formatDate(report.date)}</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/30 transition-colors"
                onClick={onClose}
              >
                <X className="h-5 w-5 text-white" />
                <span className="sr-only">Close</span>
              </motion.button>
            </div>
          </div>

          <div className="p-4 md:p-6 pt-8 md:pt-12 overflow-y-auto max-h-[calc(90vh-8rem)]">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl shadow-sm overflow-hidden"
              >
                <div className="p-4 md:p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-blue-700 flex items-center">Caving Distance</h3>
                    <div className="bg-blue-500/10 p-2 rounded-full">
                      <Pickaxe className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-blue-700">{report.caving}m</p>
                  <p className="text-xs text-blue-600/70 mt-1">Total distance excavated</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl shadow-sm overflow-hidden"
              >
                <div className="p-4 md:p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700 flex items-center">Useless Stones</h3>
                    <div className="bg-gray-500/10 p-2 rounded-full">
                      <Mountain className="h-5 w-5 text-gray-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-700">{report.uselessStones}</p>
                  <p className="text-xs text-gray-600/70 mt-1">Non-valuable materials</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl shadow-sm overflow-hidden"
              >
                <div className="p-4 md:p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-emerald-700 flex items-center">Emeralds Found</h3>
                    <div className="bg-emerald-500/10 p-2 rounded-full">
                      <Gem className="h-5 w-5 text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-emerald-700">{report.emeraldsFound}</p>
                  <p className="text-xs text-emerald-600/70 mt-1">Valuable gems extracted</p>
                </div>
              </motion.div>
            </div>

            {/* Notes Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-8"
            >
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-800">Notes</h3>
              </div>
              <div className="bg-gray-50 border border-gray-100 p-5 rounded-xl">
                <p className="text-gray-700 leading-relaxed">
                  {report.notes || "No notes available for this mining expedition."}
                </p>
              </div>
            </motion.div>

            {/* Team Members */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mb-8"
            >
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-800">Team Members</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {(report.team || []).length > 0 ? (
                  report.team.map((member, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100 px-4 py-2 rounded-full text-sm font-medium text-emerald-700"
                    >
                      {member}
                    </motion.div>
                  ))
                ) : (
                  <div className="text-gray-500">No team members assigned to this expedition.</div>
                )}
              </div>
            </motion.div>

            {/* Photos Gallery - Keeping original image rendering logic */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
              <div className="flex items-center gap-2 mb-3">
                <Gem className="h-5 w-5 text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-800">Photos</h3>
              </div>

              {loadingImages && (
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
                </div>
              )}

              {imageError && (
                <div className="bg-red-50 border border-red-100 p-4 rounded-xl text-red-600">{imageError}</div>
              )}

              {imageUrls.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  {imageUrls.map((url, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                      whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
                      className="rounded-xl overflow-hidden shadow-sm border border-gray-200 bg-white cursor-pointer group"
                      onClick={() => openLightbox(index)}
                    >
                      <div className="relative aspect-square">
                        <img
                          src={url || "/placeholder.svg"}
                          alt={`Mining site on ${formatDate(report.date)} - ${index + 1}`}
                          className="w-full h-full object-contain bg-gray-100"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = "/placeholder.svg?height=192&width=192&text=Image+Failed"
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                          <p className="text-white p-3 text-sm">Photo {index + 1}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Lightbox for full-screen image viewing */}
        <AnimatePresence>
          {activeImageIndex !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 backdrop-blur-lg z-[60] flex items-center justify-center"
              onClick={closeLightbox}
            >
              <motion.button
                className="absolute top-4 right-4 bg-black/30 backdrop-blur-sm p-2 rounded-full hover:bg-black/50 transition-colors z-[70]"
                onClick={closeLightbox}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <X className="h-6 w-6 text-white" />
                <span className="sr-only">Close</span>
              </motion.button>

              <motion.button
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 backdrop-blur-sm p-3 rounded-full hover:bg-black/50 transition-colors z-[70]"
                onClick={prevImage}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <X className="h-6 w-6 text-white rotate-45" />
                <span className="sr-only">Previous</span>
              </motion.button>

              <motion.button
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 backdrop-blur-sm p-3 rounded-full hover:bg-black/50 transition-colors z-[70]"
                onClick={nextImage}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <X className="h-6 w-6 text-white -rotate-45" />
                <span className="sr-only">Next</span>
              </motion.button>

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative max-w-[90vw] max-h-[90vh]"
              >
                <img
                  src={imageUrls[activeImageIndex] || "/placeholder.svg"}
                  alt={`Mining site on ${formatDate(report.date)} - full view`}
                  className="max-w-full max-h-[90vh] object-contain"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm text-white p-4 text-center">
                  Photo {activeImageIndex + 1} of {imageUrls.length}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}
